import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useLatest } from './useLatest';

export interface UsePollingOptions {
  /**
   * Stop polling while the browser tab is hidden and poll once right away
   * when it becomes visible again
   * @default false
   */
  pauseWhenHidden?: boolean;
}

function subscribeToVisibility(onChange: () => void): () => void {
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}

function isDocumentVisible(): boolean {
  return document.visibilityState !== 'hidden';
}

function isVisibleOnServer(): boolean {
  return true;
}

/**
 * Calls `callback` every `intervalMs` milliseconds; pass `null` to stop.
 * The latest callback is called, so passing a new function does not restart
 * the timer.
 *
 * @example
 * ```tsx
 * // Refresh while the task is still running
 * usePolling(reload, isRunning ? 2000 : null);
 *
 * // Auto-refresh a dashboard, but not in a background tab
 * usePolling(reload, 60_000, { pauseWhenHidden: true });
 * ```
 */
export function usePolling(
  callback: () => void,
  intervalMs: number | null,
  { pauseWhenHidden = false }: UsePollingOptions = {}
): void {
  const latest = useLatest(callback);
  const isVisible = useSyncExternalStore(subscribeToVisibility, isDocumentVisible, isVisibleOnServer);
  const isPaused = pauseWhenHidden && !isVisible;
  // Set while polling is paused: the data is stale once the tab is shown again
  const missedPollRef = useRef(false);

  useEffect(() => {
    if (intervalMs === null) return;
    if (isPaused) {
      missedPollRef.current = true;
      return;
    }
    if (missedPollRef.current) {
      missedPollRef.current = false;
      latest.current();
    }
    const timer = setInterval(() => latest.current(), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs, isPaused, latest]);
}
